import CELL_ATTRIBUTES, { MISC_KEYS } from "./CellAttributesDefinition";
import { ISlotData, TContainerLengths } from "open-vessel-definition";

import { createSvgText } from "../shipViews/helpers/createSvgText";
import { roundDec } from "@tedivo/tedivo-pure-helpers";

export function createSlotCapabilitiesSvg({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  slotWidth,
  slotHeight,
  fontSize,
  slotData,
  labelWidth = 12,
  labelHeight = 12,
  labelGap = 0,
  centered = false,
  isDarkMode = false,
  symbolPrefix = "",
  symbolsCache,
}: ICreateSlotCapabilitiesProps): SVGGElement {
  // Main group
  const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
  const svgGCentered = centered
    ? document.createElementNS("http://www.w3.org/2000/svg", "g")
    : null;

  // Dimensions for labels
  //const maxLabelsV = Math.floor(slotHeight / labelHeight);
  const startY = 1;
  let x = labelGap,
    y = startY;

  // 1. acceptsContainers
  if (slotData.sizes) {
    (Object.keys(slotData.sizes) as unknown as TContainerLengths[])
      .filter((contLen) => typeof slotData.sizes[contLen])

      .forEach((contLen) => {
        const symbolsCacheName = `${symbolPrefix}Size_${slotWidth}${slotHeight}${fontSize}_${contLen}Symbol`;

        const coneIsRequired =
          typeof slotData.sizes[contLen] === "object" &&
          (slotData.sizes[contLen] as { cone: 1 | 0 }).cone === 1;

        const sizeIsAllowed = slotData.sizes[contLen] === 1 || coneIsRequired;

        if (sizeIsAllowed) {
          const ca = CELL_ATTRIBUTES.sizes[contLen];
          addTextBox(
            symbolsCacheName,
            svgGCentered || svgGroup,
            String(ca.text),
            x,
            y,
            isDarkMode ? ca.darkBgColor || ca.bgColor : ca.bgColor,
            isDarkMode ? ca.darkColor || ca.color : ca.color,
            !coneIsRequired,
          );

          if (coneIsRequired) {
            const conePath = document.createElementNS("http://www.w3.org/2000/svg", "path");
            const yCone = roundDec(y - labelHeight * (fontSize > 0 ? 0.05 : -0.45), 2);
            const xCone = roundDec(x + (fontSize > 0 ? 0 : labelWidth * -0.25), 2);

            conePath.setAttribute("fill", ca.color);
            conePath.setAttribute("stroke-width", "0");
            conePath.setAttribute(
              "d",
              `M${labelWidth * 0.8 + xCone},${yCone - 1} L${labelWidth * 0.6 + xCone},${
                labelHeight * 0.25 + yCone
              } L${labelWidth + xCone},${labelHeight * 0.25 + yCone} Z`,
            );
            (svgGCentered || svgGroup).appendChild(conePath);

            y += labelHeight + labelGap;
            if (y + labelHeight > slotHeight) {
              y = startY;
              x += labelWidth + labelGap;
            }
          }
        }
      });
  }

  // 2. Misc
  MISC_KEYS.filter((k) => !!slotData[k]).forEach((k) => {
    const ca = CELL_ATTRIBUTES.misc[k];

    const symbolsCacheName = `${symbolPrefix}Misc${slotWidth}${slotHeight}${fontSize}_${k}Symbol`;
    const addFn = k === "reefer" ? addReeferIcon : addTextBox;
    addFn(
      symbolsCacheName,
      svgGCentered || svgGroup,
      String(ca.text),
      x,
      y,
      isDarkMode ? ca.darkBgColor || ca.bgColor : ca.bgColor,
      isDarkMode ? ca.darkColor || ca.color : ca.color,
    );
  });

  // 3. Restricted
  if (slotData.restricted) {
    const ca = CELL_ATTRIBUTES.restricted.restricted;
    const symbolsCacheName = `Restricted${symbolPrefix}${slotWidth}${slotHeight}${fontSize}Symbol`;

    addRestrictedIcon(
      symbolsCacheName,
      svgGCentered || svgGroup,
      isDarkMode ? ca.darkBgColor || ca.bgColor : ca.bgColor,
    );
  }

  // IF CENTERED ***
  if (svgGCentered) {
    if (!slotData.restricted) {
      const gWidth = x + (y === startY ? startY : labelWidth);
      svgGCentered.setAttribute("transform", `translate(${(slotWidth - gWidth) * 0.5}, 0)`);
    }

    svgGroup.appendChild(svgGCentered);
  }

  return svgGroup;

  function addTextBox(
    symbolsCacheName: string,
    group: SVGGElement,
    text: string,
    xPos: number,
    yPos: number,
    bgColor: string,
    color: string,
    addUpCoordinates = true,
  ) {
    if (!symbolsCache.has(symbolsCacheName)) {
      const symbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol");
      symbol.setAttribute("id", symbolsCacheName);

      const rect = document.createElementNS("http://www.w3.org/2000/svg", "path");

      rect.setAttribute("d", `M0,0 h${labelWidth} v${labelHeight} h-${labelWidth} z`);

      rect.setAttribute("fill", bgColor);

      symbol.appendChild(rect);

      if (fontSize > 0) {
        const svgT = createSvgText({
          text,
          x: Math.round(xPos - x + labelWidth * 0.5),
          y: Math.ceil(yPos - y + labelHeight * 0.5) + 1,
          textColor: color,
          fontSize,
        });
        symbol.appendChild(svgT);
      }

      symbol.setAttribute("title", text);
      symbolsCache.set(symbolsCacheName, symbol);
    }

    const useSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");

    useSvg.setAttribute("href", `#${symbolsCacheName}`);
    useSvg.setAttribute("title", text);
    useSvg.setAttribute("transform", `translate(${x}, ${y})`);
    group.appendChild(useSvg);

    if (addUpCoordinates) {
      y += labelHeight + labelGap;
      if (y + labelHeight > slotHeight) {
        y = startY;
        x += labelWidth + labelGap;
      }
    }
  }

  function addRestrictedIcon(symbolsCacheName: string, group: SVGGElement, bgColor: string) {
    if (!symbolsCache.has(symbolsCacheName)) {
      const symbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol");

      symbol.setAttribute("id", symbolsCacheName);

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      const x1 = slotWidth * 0.5 - labelHeight * 0.5;
      const x2 = x1 + labelHeight;
      const y1 = slotHeight * 0.5 - labelHeight * 0.5;
      const y2 = y1 + labelHeight;

      path.setAttribute("d", `M${x1},${y1} L${x2},${y2} M${x1},${y2} L${x2},${y1}`);
      path.setAttribute("fill", bgColor);
      path.setAttribute("stroke", bgColor);
      path.setAttribute("stroke-width", slotWidth < 30 ? "1.5" : "2.5");

      symbol.appendChild(path);

      symbolsCache.set(symbolsCacheName, symbol);
    }

    const useSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");

    useSvg.setAttribute("href", `#${symbolsCacheName}`);
    group.appendChild(useSvg);
  }

  function addReeferIcon(
    symbolsCacheName: string,
    group: SVGGElement,
    text: string,
    xPos: number,
    yPos: number,
    bgColor: string,
    color: string,
    addUpCoordinates = true,
  ) {
    if (!symbolsCache.has(symbolsCacheName)) {
      const reeferSymbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol");

      reeferSymbol.setAttribute("id", symbolsCacheName);

      const reeferPath = document.createElementNS("http://www.w3.org/2000/svg", "path");

      reeferPath.setAttribute("d", reeferIconPath);

      reeferSymbol.appendChild(reeferPath);

      symbolsCache.set(symbolsCacheName, reeferSymbol);
    }

    const useSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");

    useSvg.setAttribute("href", `#${symbolsCacheName}`);

    const k = slotWidth / 500; // 300 is the original size of the reefer icon

    //slotWidth

    useSvg.setAttribute("fill", bgColor);
    useSvg.setAttribute("stroke", bgColor);
    useSvg.setAttribute("stroke-width", "0");
    useSvg.setAttribute("transform", `translate(${xPos}, ${yPos}) scale(${k}, ${k}) `);

    group.appendChild(useSvg);

    if (addUpCoordinates) {
      y += labelHeight + labelGap;
      if (y + labelHeight > slotHeight) {
        y = startY;
        x += labelWidth + labelGap;
      }
    }
  }
}

interface ICreateSlotCapabilitiesProps {
  slotWidth: number;
  slotHeight: number;
  fontSize: number;
  slotData: ISlotData;
  labelWidth?: number;
  labelHeight?: number;
  labelGap?: number;
  centered?: boolean;
  isDarkMode?: boolean;
  symbolPrefix?: string;
  symbolsCache: Map<string, SVGSymbolElement>;
}

export const reeferIconPath = `M149.275,90.325L118.235,98.638L106.27,91.736L106.27,78.784L118.098,71.992L149.275,80.345C149.901,80.512
150.531,80.595
151.155,80.595C154.357,80.595
157.288,78.456
158.153,75.217C159.19,71.345
156.895,67.367
153.024,66.331L135.932,61.751L151.689,52.705C155.161,50.711
156.363,46.277
154.368,42.802C152.366,39.328
147.941,38.124
144.467,40.124L128.678,49.191L133.257,32.094C134.293,28.223
132.001,24.244
128.128,23.208C124.281,22.199
120.286,24.469
119.249,28.336L110.93,59.378L99.027,66.214L87.744,59.69L87.744,46.002L110.561,23.185C113.394,20.351
113.394,15.761
110.561,12.927C107.728,10.093
103.139,10.093
100.303,12.927L87.744,25.485L87.744,7.254C87.745,3.248
84.496,0
80.49,0C76.485,0
73.236,3.248
73.236,7.254L73.236,25.396L60.768,12.927C57.935,10.093
53.345,10.093
50.51,12.927C47.677,15.761
47.677,20.351
50.51,23.185L73.236,45.911L73.236,59.743L62.039,66.203L50.136,59.374L41.823,28.337C40.788,24.469
36.812,22.193
32.944,23.208C29.073,24.244
26.776,28.224
27.813,32.094L32.391,49.189L16.604,40.124C13.123,38.124
8.693,39.325
6.701,42.804C4.708,46.279
5.91,50.71
9.384,52.705L25.138,61.751L8.046,66.33C4.175,67.366
1.879,71.345
2.916,75.216C3.783,78.456
6.713,80.595
9.916,80.595C10.539,80.595
11.169,80.512
11.798,80.345L42.979,71.988L54.794,78.772L54.806,91.736L42.837,98.65L11.798,90.325C7.954,89.316
3.954,91.588
2.916,95.455C1.879,99.325
4.175,103.304
8.046,104.335L25.076,108.902L9.366,117.971C5.898,119.976
4.708,124.409
6.713,127.877C8.057,130.211
10.494,131.504
12.999,131.504C14.228,131.504
15.476,131.198
16.619,130.529L32.409,121.417L27.813,138.577C26.776,142.454
29.073,146.421
32.944,147.463C33.571,147.633
34.2,147.713
34.825,147.713C38.026,147.713
40.956,145.571
41.823,142.33L50.178,111.159L62.06,104.303L73.236,110.752L73.236,124.761L50.51,147.488C47.677,150.321
47.677,154.91
50.51,157.745C51.927,159.162
53.78,159.864
55.639,159.864C57.492,159.864
59.351,159.162
60.768,157.745L73.236,145.277L73.236,163.413C73.236,167.426
76.485,170.667
80.49,170.667C84.496,170.667
87.745,167.425
87.745,163.413L87.745,145.186L100.304,157.745C101.72,159.162
103.573,159.864
105.433,159.864C107.292,159.864
109.145,159.162
110.562,157.745C113.394,154.911
113.394,150.321
110.562,147.488L87.745,124.671L87.745,110.808L99.017,104.302L110.896,111.158L119.25,142.329C120.117,145.571
123.046,147.713
126.247,147.713C126.869,147.713
127.501,147.632
128.129,147.462C132.002,146.42
134.293,142.453
133.258,138.576L128.657,121.416L144.453,130.528C145.596,131.196
146.844,131.503
148.074,131.503C150.578,131.503
153.015,130.211
154.359,127.876C156.364,124.408
155.175,119.976
151.706,117.971L135.996,108.903L153.025,104.335C156.896,103.305
159.192,99.325
158.154,95.455C157.124,91.588
153.14,89.311
149.275,90.325ZM91.762,91.736L80.535,98.22L69.317,91.736L69.304,78.76L80.535,72.281L91.762,78.77L91.762,91.736Z`;
